import { FileImageOutlined, StopOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Alert, Image, Skeleton, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { errorMessage } from '../../api/client';

interface Props {
  label: string;
  exists: boolean;
  /** Fetches the file as an object URL. Only called when `exists` is true. */
  fetchUrl: () => Promise<string>;
  /** Renders a <video> instead of an image. */
  isVideo?: boolean;
}

/**
 * Kept deliberately compact: these are reference thumbnails, not the main content of the page.
 * The CNIC image stays click-to-zoom for reading the small print.
 */
const MEDIA_HEIGHT = 170;

/**
 * Renders a captured document inline, for an investor or a dealer.
 *
 * The file sits behind JWT auth, so it cannot be pointed at with a plain <img src> — the bytes are
 * fetched once into an object URL, which must be revoked on unmount or the blob leaks. The fetcher
 * is injected so both sides of the book share one component.
 *
 * Read-only by design: the CNIC scan and signed agreement video are the evidence the account was
 * opened on agreed terms, so they are captured once at creation and never replaced from here.
 */
export default function DocumentCard({ label, exists, fetchUrl, isVideo = false }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(exists);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!exists) {
      setUrl(null);
      setLoading(false);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    setLoading(true);
    setError(null);

    fetchUrl()
      .then((created) => {
        objectUrl = created;
        if (cancelled) URL.revokeObjectURL(created);
        else setUrl(created);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // fetchUrl is a fresh closure on every render, so it is deliberately not a dependency —
    // including it would refetch the file on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exists]);

  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 10,
        background: '#fafafa',
        height: '100%',
      }}
    >
      <Space size={6} style={{ marginBottom: 6 }}>
        <span style={{ color: exists ? '#1677ff' : '#bfbfbf', fontSize: 13 }}>
          {isVideo ? <VideoCameraOutlined /> : <FileImageOutlined />}
        </span>
        <Typography.Text strong style={{ fontSize: 13 }}>
          {label}
        </Typography.Text>
      </Space>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: MEDIA_HEIGHT,
          background: '#fff',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        {!exists ? (
          <Space direction="vertical" align="center" size={2}>
            <StopOutlined style={{ fontSize: 20, color: '#d9d9d9' }} />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Not provided at account opening
            </Typography.Text>
          </Space>
        ) : loading ? (
          <Skeleton.Image active style={{ width: 120, height: 96 }} />
        ) : error ? (
          <Alert type="error" showIcon message="Could not load this file" description={error} style={{ margin: 8 }} />
        ) : isVideo ? (
          // preload="metadata" so the poster frame appears without pulling the whole clip.
          <video
            src={url ?? undefined}
            controls
            preload="metadata"
            style={{ maxWidth: '100%', maxHeight: MEDIA_HEIGHT, background: '#000' }}
          />
        ) : (
          // antd Image keeps it inline but still allows click-to-zoom for reading CNIC details.
          <Image
            src={url ?? undefined}
            alt={label}
            height={MEDIA_HEIGHT}
            style={{ objectFit: 'contain' }}
            preview={{ mask: 'Click to enlarge' }}
          />
        )}
      </div>
    </div>
  );
}
